// AdminEmployeeManagement.js - Complete Working Version with Delete Functionality
import React, { useState, useEffect } from "react";
import "./AdminEmployeeManagement.css";
import Sidebar from "./Sidebar/Sidebar";

const AdminEmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  // New state for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    bloodGroup: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    emergencyContact: "",
    emergencyContactRelation: "",
    emergencyContactPhone: "",
    employeeId: "",
    joiningDate: "",
    role: "employee",
    department: "",
    subDepartment: "",
    position: "",
    performanceRating: "",
    leaveBalance: "",
    workingMode: "onsite",
    projectName: "",
    clientName: "",
    technicalSkills: [],
    education: [],
    experience: [],
    salary: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
    panNumber: "",
    documents: {
      idProof: null,
      addressProof: null,
      resume: null,
      others: [],
    },
  });

  const [newSkill, setNewSkill] = useState("");
  const [newEducation, setNewEducation] = useState({
    degree: "",
    institution: "",
    year: "",
    percentage: "",
  });
  const [newExperience, setNewExperience] = useState({
    company: "",
    position: "",
    duration: "",
    description: "",
  });

  const getAuthToken = () => {
    return localStorage.getItem("authToken");
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/admin/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (data.success) {
        setEmployees(data.users);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      alert("Error fetching employees");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeDetails = async (employeeId) => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/admin/users/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSelectedEmployee(data.user);
        setShowDetailModal(true);
        setActiveTab("personal");
      } else {
        alert("Error fetching employee details");
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
      alert("Error fetching employee details");
    } finally {
      setLoading(false);
    }
  };

  // New function to handle employee deletion
  const handleDeleteEmployee = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/admin/users/${employeeToDelete}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("Employee deleted successfully!");
        setShowDeleteConfirm(false);
        setEmployeeToDelete(null);
        fetchEmployees(); // Refresh employee list
      } else {
        alert(data.message || "Error deleting employee");
      }
    } catch (error) {
      console.error("Error deleting employee:", error);
      alert("Error deleting employee");
    } finally {
      setLoading(false);
    }
  };

  // Function to show delete confirmation modal
  const confirmDelete = (userId) => {
    setEmployeeToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = getAuthToken();

      const formDataToSend = new FormData();

      const addToFormData = (key, value) => {
        if (value !== "" && value !== null && value !== undefined) {
          formDataToSend.append(key, value);
        }
      };

      Object.keys(formData).forEach((key) => {
        if (
          key !== "documents" &&
          key !== "technicalSkills" &&
          key !== "education" &&
          key !== "experience"
        ) {
          if (["gender", "bloodGroup", "performanceRating"].includes(key)) {
            addToFormData(key, formData[key]);
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      formDataToSend.append(
        "technicalSkills",
        JSON.stringify(formData.technicalSkills)
      );
      formDataToSend.append("education", JSON.stringify(formData.education));
      formDataToSend.append("experience", JSON.stringify(formData.experience));

      if (formData.documents.idProof) {
        formDataToSend.append("idProof", formData.documents.idProof);
      }
      if (formData.documents.addressProof) {
        formDataToSend.append("addressProof", formData.documents.addressProof);
      }
      if (formData.documents.resume) {
        formDataToSend.append("resume", formData.documents.resume);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/admin/users`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend,
        }
      );

      const data = await response.json();
      if (data.success) {
        alert("Employee created successfully!");
        resetForm();
        setShowAddForm(false);
        fetchEmployees();
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error("Error creating employee:", error);
      alert("Error creating employee");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      phone: "",
      dateOfBirth: "",
      gender: "",
      bloodGroup: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      emergencyContact: "",
      emergencyContactRelation: "",
      emergencyContactPhone: "",
      employeeId: "",
      joiningDate: "",
      role: "employee",
      department: "",
      subDepartment: "",
      position: "",
      performanceRating: "",
      leaveBalance: "",
      workingMode: "onsite",
      projectName: "",
      clientName: "",
      technicalSkills: [],
      education: [],
      experience: [],
      salary: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
      panNumber: "",
      documents: {
        idProof: null,
        addressProof: null,
        resume: null,
        others: [],
      },
    });
    setActiveTab("personal");
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e, docType) => {
    setFormData({
      ...formData,
      documents: {
        ...formData.documents,
        [docType]: e.target.files[0],
      },
    });
  };

  const addSkill = () => {
    if (newSkill.trim()) {
      setFormData({
        ...formData,
        technicalSkills: [...formData.technicalSkills, newSkill.trim()],
      });
      setNewSkill("");
    }
  };

  const removeSkill = (index) => {
    setFormData({
      ...formData,
      technicalSkills: formData.technicalSkills.filter((_, i) => i !== index),
    });
  };

  const addEducation = () => {
    if (newEducation.degree && newEducation.institution) {
      setFormData({
        ...formData,
        education: [...formData.education, newEducation],
      });
      setNewEducation({
        degree: "",
        institution: "",
        year: "",
        percentage: "",
      });
    }
  };

  const removeEducation = (index) => {
    setFormData({
      ...formData,
      education: formData.education.filter((_, i) => i !== index),
    });
  };

  const addExperience = () => {
    if (newExperience.company && newExperience.position) {
      setFormData({
        ...formData,
        experience: [...formData.experience, newExperience],
      });
      setNewExperience({
        company: "",
        position: "",
        duration: "",
        description: "",
      });
    }
  };

  const removeExperience = (index) => {
    setFormData({
      ...formData,
      experience: formData.experience.filter((_, i) => i !== index),
    });
  };

  const handleToggleStatus = async (userId) => {
    try {
      const token = getAuthToken();
      await fetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/admin/users/${userId}/toggle-status`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchEmployees();
    } catch (error) {
      console.error("Error toggling status:", error);
      alert("Error updating employee status");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const downloadDocument = (docPath, docName) => {
    if (!docPath) return;
    window.open(`/${docPath}`, "_blank");
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const tabs = [
    { id: "personal", label: "Personal Details", icon: "fas fa-user" },
    { id: "address", label: "Address & Contact", icon: "fas fa-home" },
    { id: "employment", label: "Employment", icon: "fas fa-briefcase" },
    {
      id: "education",
      label: "Education & Experience",
      icon: "fas fa-graduation-cap",
    },
    { id: "financial", label: "Financial", icon: "fas fa-money-bill" },
    { id: "documents", label: "Documents", icon: "fas fa-file-alt" },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "personal":
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter password"
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Gender</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Blood Group</label>
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleInputChange}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
            </div>
          </div>
        );

      case "address":
        return (
          <div className="tab-content">
            <div className="form-group">
              <label>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter full address"
                rows="3"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Enter city"
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="Enter state"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>ZIP Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  placeholder="Enter ZIP code"
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="Enter country"
                />
              </div>
            </div>

            <h4>Emergency Contact</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Emergency Contact Name</label>
                <input
                  type="text"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  placeholder="Enter emergency contact name"
                />
              </div>
              <div className="form-group">
                <label>Relation</label>
                <input
                  type="text"
                  name="emergencyContactRelation"
                  value={formData.emergencyContactRelation}
                  onChange={handleInputChange}
                  placeholder="e.g., Father, Mother, Spouse"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Emergency Contact Phone</label>
              <input
                type="tel"
                name="emergencyContactPhone"
                value={formData.emergencyContactPhone}
                onChange={handleInputChange}
                placeholder="Enter emergency contact phone"
              />
            </div>
          </div>
        );

      case "employment":
        return (
          <div className="tab-content">
            <div className="form-row">
              <div className="form-group">
                <label>Employee ID</label>
                <input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleInputChange}
                  placeholder="e.g., EMP001"
                />
              </div>
              <div className="form-group">
                <label>Joining Date</label>
                <input
                  type="date"
                  name="joiningDate"
                  value={formData.joiningDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Role *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="employee">Employee</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="hr">HR</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g., IT, Marketing"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sub Department</label>
                <input
                  type="text"
                  name="subDepartment"
                  value={formData.subDepartment}
                  onChange={handleInputChange}
                  placeholder="e.g., Frontend, Backend"
                />
              </div>
              <div className="form-group">
                <label>Position</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  placeholder="e.g., Software Developer"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Working Mode</label>
                <select
                  name="workingMode"
                  value={formData.workingMode}
                  onChange={handleInputChange}
                >
                  <option value="onsite">Onsite</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="form-group">
                <label>Performance Rating</label>
                <select
                  name="performanceRating"
                  value={formData.performanceRating}
                  onChange={handleInputChange}
                >
                  <option value="">Select Rating</option>
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="satisfactory">Satisfactory</option>
                  <option value="needs-improvement">Needs Improvement</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Project Name</label>
                <input
                  type="text"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleInputChange}
                  placeholder="Current project name"
                />
              </div>
              <div className="form-group">
                <label>Client Name</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  placeholder="Client name"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Leave Balance (Days)</label>
              <input
                type="number"
                name="leaveBalance"
                value={formData.leaveBalance}
                onChange={handleInputChange}
                placeholder="Available leave days"
              />
            </div>

            <h4>Technical Skills</h4>
            <div className="skills-section">
              <div className="add-skill">
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Add technical skill"
                  onKeyPress={(e) => e.key === "Enter" && addSkill()}
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="btn btn-sm btn-primary"
                >
                  Add
                </button>
              </div>
              <div className="skills-list">
                {formData.technicalSkills.map((skill, index) => (
                  <span key={index} className="skill-tag">
                    {skill}
                    <button type="button" onClick={() => removeSkill(index)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      case "education":
        return (
          <div className="tab-content">
            <h4>Education</h4>
            <div className="education-section">
              <div className="add-education">
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      value={newEducation.degree}
                      onChange={(e) =>
                        setNewEducation({
                          ...newEducation,
                          degree: e.target.value,
                        })
                      }
                      placeholder="Degree"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      value={newEducation.institution}
                      onChange={(e) =>
                        setNewEducation({
                          ...newEducation,
                          institution: e.target.value,
                        })
                      }
                      placeholder="Institution"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      value={newEducation.year}
                      onChange={(e) =>
                        setNewEducation({
                          ...newEducation,
                          year: e.target.value,
                        })
                      }
                      placeholder="Year"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      value={newEducation.percentage}
                      onChange={(e) =>
                        setNewEducation({
                          ...newEducation,
                          percentage: e.target.value,
                        })
                      }
                      placeholder="Percentage/CGPA"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addEducation}
                  className="btn btn-sm btn-primary"
                >
                  Add Education
                </button>
              </div>
              <div className="education-list">
                {formData.education.map((edu, index) => (
                  <div key={index} className="education-item">
                    <div>
                      <strong>{edu.degree}</strong> from {edu.institution}
                      {edu.year && ` (${edu.year})`}
                      {edu.percentage && ` - ${edu.percentage}`}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeEducation(index)}
                      className="btn btn-sm btn-danger"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <h4>Experience</h4>
            <div className="experience-section">
              <div className="add-experience">
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      value={newExperience.company}
                      onChange={(e) =>
                        setNewExperience({
                          ...newExperience,
                          company: e.target.value,
                        })
                      }
                      placeholder="Company Name"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      value={newExperience.position}
                      onChange={(e) =>
                        setNewExperience({
                          ...newExperience,
                          position: e.target.value,
                        })
                      }
                      placeholder="Position"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <input
                      type="text"
                      value={newExperience.duration}
                      onChange={(e) =>
                        setNewExperience({
                          ...newExperience,
                          duration: e.target.value,
                        })
                      }
                      placeholder="Duration (e.g., Jan 2020 - Dec 2022)"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <textarea
                    value={newExperience.description}
                    onChange={(e) =>
                      setNewExperience({
                        ...newExperience,
                        description: e.target.value,
                      })
                    }
                    placeholder="Job Description"
                    rows="3"
                  />
                </div>
                <button
                  type="button"
                  onClick={addExperience}
                  className="btn btn-sm btn-primary"
                >
                  Add Experience
                </button>
              </div>
              <div className="experience-list">
                {formData.experience.map((exp, index) => (
                  <div key={index} className="experience-item">
                    <div>
                      <strong>{exp.position}</strong> at {exp.company}
                      {exp.duration && ` (${exp.duration})`}
                      {exp.description && <p>{exp.description}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeExperience(index)}
                      className="btn btn-sm btn-danger"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "financial":
        return (
          <div className="tab-content">
            <h4>Salary Information</h4>
            <div className="form-group">
              <label>Salary (Per Month/Year)</label>
              <input
                type="number"
                name="salary"
                value={formData.salary}
                onChange={handleInputChange}
                placeholder="Enter salary amount"
              />
            </div>

            <h4>Bank Details</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="Enter bank name"
                />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleInputChange}
                  placeholder="Enter account number"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>IFSC Code</label>
                <input
                  type="text"
                  name="ifscCode"
                  value={formData.ifscCode}
                  onChange={handleInputChange}
                  placeholder="Enter IFSC code"
                />
              </div>
              <div className="form-group">
                <label>PAN Number</label>
                <input
                  type="text"
                  name="panNumber"
                  value={formData.panNumber}
                  onChange={handleInputChange}
                  placeholder="Enter PAN number"
                />
              </div>
            </div>
          </div>
        );

      case "documents":
        return (
          <div className="tab-content">
            <div className="form-group">
              <label>ID Proof (Aadhar, Passport, etc.)</label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, "idProof")}
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>

            <div className="form-group">
              <label>Address Proof</label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, "addressProof")}
                accept=".pdf,.jpg,.jpeg,.png"
              />
            </div>

            <div className="form-group">
              <label>Resume</label>
              <input
                type="file"
                onChange={(e) => handleFileChange(e, "resume")}
                accept=".pdf,.doc,.docx"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <div
        style={{
          marginLeft: "250px",
          flex: 1,
          padding: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div className="admin-employee-management">
          <div className="section-header">
            <h2>
              <i className="fas fa-users"></i>
              Employee Management
            </h2>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <i className="fas fa-plus"></i>
              {showAddForm ? "Cancel" : "Add Employee"}
            </button>
          </div>

          {/* Add Employee Form */}
          {showAddForm && (
            <div className="employee-form-container">
              <div className="form-header">
                <h3>Add New Employee</h3>
              </div>

              {/* Tabs */}
              <div className="form-tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`tab-button ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleAddEmployee} className="employee-form">
                {renderTabContent()}

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowAddForm(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Creating..." : "Create Employee"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Employee List */}
          <div className="employees-list-container">
            <div className="table-header">
              <h3>Current Employees ({employees.length})</h3>
            </div>

            {loading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading employees...</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="employees-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Employee ID</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((employee) => (
                      <tr key={employee._id}>
                        <td>
                          <div className="employee-name">
                            <div className="employee-avatar">
                              {employee.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span>{employee.name}</span>
                          </div>
                        </td>
                        <td>{employee.email}</td>
                        <td>
                          <span className={`role-badge role-${employee.role}`}>
                            {employee.role}
                          </span>
                        </td>
                        <td>{employee.department || "-"}</td>
                        <td>{employee.employeeId || "-"}</td>
                        <td>
                          <span
                            className={`status-badge ${
                              employee.isActive ? "active" : "inactive"
                            }`}
                          >
                            {employee.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {new Date(employee.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => fetchEmployeeDetails(employee._id)}
                              className="btn btn-sm btn-info"
                              title="View Details"
                            >
                              <i className="fas fa-eye"></i>
                              View
                            </button>
                            <button
                              onClick={() => handleToggleStatus(employee._id)}
                              className={`btn btn-sm ${
                                employee.isActive
                                  ? "btn-warning"
                                  : "btn-success"
                              }`}
                            >
                              {employee.isActive ? "Deactivate" : "Activate"}
                            </button>
                            {/* New Delete Button */}
                            <button
                              onClick={() => confirmDelete(employee._id)}
                              className="btn btn-sm btn-danger"
                              title="Delete Employee"
                            >
                              <i className="fas fa-trash"></i>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {employees.length === 0 && (
                  <div className="empty-state">
                    <i className="fas fa-user-plus"></i>
                    <h3>No employees found</h3>
                    <p>Start by adding your first employee</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Employee Detail Modal */}
        {showDetailModal && selectedEmployee && (
          <div
            className="modal-overlay"
            onClick={() => setShowDetailModal(false)}
          >
            <div
              className="modal-content employee-detail-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="employee-summary">
                  <div className="employee-avatar-large">
                    {selectedEmployee.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div className="employee-basic-info">
                    <h2>{selectedEmployee.name}</h2>
                    <p className="employee-meta">
                      {selectedEmployee.position} •{" "}
                      {selectedEmployee.department}
                      {selectedEmployee.employeeId &&
                        ` • ID: ${selectedEmployee.employeeId}`}
                    </p>
                    <div className="status-badges">
                      <span
                        className={`role-badge role-${selectedEmployee.role}`}
                      >
                        {selectedEmployee.role}
                      </span>
                      <span
                        className={`status-badge ${
                          selectedEmployee.isActive ? "active" : "inactive"
                        }`}
                      >
                        {selectedEmployee.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  className="close-btn"
                  onClick={() => setShowDetailModal(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              {/* Detail Tabs */}
              <div className="detail-tabs">
                {[
                  { id: "personal", label: "Personal", icon: "fas fa-user" },
                  { id: "contact", label: "Contact", icon: "fas fa-home" },
                  {
                    id: "employment",
                    label: "Employment",
                    icon: "fas fa-briefcase",
                  },
                  { id: "skills", label: "Skills", icon: "fas fa-code" },
                  {
                    id: "education",
                    label: "Education",
                    icon: "fas fa-graduation-cap",
                  },
                  {
                    id: "financial",
                    label: "Financial",
                    icon: "fas fa-money-bill",
                  },
                  {
                    id: "documents",
                    label: "Documents",
                    icon: "fas fa-file-alt",
                  },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    className={`tab-button ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="detail-content">
                {activeTab === "personal" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Personal Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Full Name</label>
                        <span>{selectedEmployee.name || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Email</label>
                        <span>{selectedEmployee.email || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Phone</label>
                        <span>{selectedEmployee.phone || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Date of Birth</label>
                        <span>{formatDate(selectedEmployee.dateOfBirth)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Gender</label>
                        <span className="capitalize">
                          {selectedEmployee.gender || "-"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Blood Group</label>
                        <span>{selectedEmployee.bloodGroup || "-"}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "contact" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Address Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item full-width">
                        <label>Address</label>
                        <span>{selectedEmployee.address || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>City</label>
                        <span>{selectedEmployee.city || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>State</label>
                        <span>{selectedEmployee.state || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>ZIP Code</label>
                        <span>{selectedEmployee.zipCode || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Country</label>
                        <span>{selectedEmployee.country || "-"}</span>
                      </div>
                    </div>

                    <h3>Emergency Contact</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Contact Person</label>
                        <span>{selectedEmployee.emergencyContact || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Relation</label>
                        <span>
                          {selectedEmployee.emergencyContactRelation || "-"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Phone</label>
                        <span>
                          {selectedEmployee.emergencyContactPhone || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "employment" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Employment Details</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Employee ID</label>
                        <span>{selectedEmployee.employeeId || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Joining Date</label>
                        <span>{formatDate(selectedEmployee.joiningDate)}</span>
                      </div>
                      <div className="detail-item">
                        <label>Role</label>
                        <span
                          className={`role-badge role-${selectedEmployee.role}`}
                        >
                          {selectedEmployee.role}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Department</label>
                        <span>{selectedEmployee.department || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Sub Department</label>
                        <span>{selectedEmployee.subDepartment || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Position</label>
                        <span>{selectedEmployee.position || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Working Mode</label>
                        <span
                          className={`working-mode ${selectedEmployee.workingMode}`}
                        >
                          {selectedEmployee.workingMode || "-"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Performance Rating</label>
                        <span
                          className={`performance-rating ${selectedEmployee.performanceRating}`}
                        >
                          {selectedEmployee.performanceRating || "-"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Leave Balance</label>
                        <span>{selectedEmployee.leaveBalance || 0} days</span>
                      </div>
                      <div className="detail-item">
                        <label>Project Name</label>
                        <span>{selectedEmployee.projectName || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Client Name</label>
                        <span>{selectedEmployee.clientName || "-"}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "skills" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Technical Skills</h3>
                    <div className="skills-container">
                      {selectedEmployee.technicalSkills &&
                      selectedEmployee.technicalSkills.length > 0 ? (
                        selectedEmployee.technicalSkills.map((skill, index) => (
                          <span key={index} className="skill-tag">
                            {skill}
                          </span>
                        ))
                      ) : (
                        <span className="no-data">No skills added</span>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "education" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Education</h3>
                    <div className="timeline-container">
                      {selectedEmployee.education &&
                      selectedEmployee.education.length > 0 ? (
                        selectedEmployee.education.map((edu, index) => (
                          <div key={index} className="timeline-item">
                            <div className="timeline-content">
                              <h4>{edu.degree || "Degree"}</h4>
                              <p className="institution">
                                {edu.institution || "Institution"}
                              </p>
                              <div className="timeline-meta">
                                {edu.year && (
                                  <span className="year">{edu.year}</span>
                                )}
                                {edu.percentage && (
                                  <span className="percentage">
                                    {" "}
                                    • {edu.percentage}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <span className="no-data">No education records</span>
                      )}
                    </div>

                    <h3>Work Experience</h3>
                    <div className="timeline-container">
                      {selectedEmployee.experience &&
                      selectedEmployee.experience.length > 0 ? (
                        selectedEmployee.experience.map((exp, index) => (
                          <div key={index} className="timeline-item">
                            <div className="timeline-content">
                              <h4>{exp.position || "Position"}</h4>
                              <p className="company">
                                {exp.company || "Company"}
                              </p>
                              {exp.duration && (
                                <p className="duration">{exp.duration}</p>
                              )}
                              {exp.description && (
                                <p className="description">{exp.description}</p>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <span className="no-data">No work experience</span>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === "financial" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Salary Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Salary</label>
                        <span>
                          {selectedEmployee.salary
                            ? `₹${selectedEmployee.salary.toLocaleString()}`
                            : "-"}
                        </span>
                      </div>
                    </div>

                    <h3>Bank Details</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Bank Name</label>
                        <span>{selectedEmployee.bankName || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>Account Number</label>
                        <span>{selectedEmployee.accountNumber || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>IFSC Code</label>
                        <span>{selectedEmployee.ifscCode || "-"}</span>
                      </div>
                      <div className="detail-item">
                        <label>PAN Number</label>
                        <span>{selectedEmployee.panNumber || "-"}</span>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "documents" && selectedEmployee && (
                  <div className="detail-section">
                    <h3>Documents</h3>
                    <div className="documents-grid">
                      <div className="document-item">
                        <label>ID Proof</label>
                        {selectedEmployee.documents?.idProof ? (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() =>
                              downloadDocument(
                                selectedEmployee.documents.idProof,
                                "ID Proof"
                              )
                            }
                          >
                            <i className="fas fa-download"></i>
                            Download
                          </button>
                        ) : (
                          <span className="no-document">Not uploaded</span>
                        )}
                      </div>
                      <div className="document-item">
                        <label>Address Proof</label>
                        {selectedEmployee.documents?.addressProof ? (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() =>
                              downloadDocument(
                                selectedEmployee.documents.addressProof,
                                "Address Proof"
                              )
                            }
                          >
                            <i className="fas fa-download"></i>
                            Download
                          </button>
                        ) : (
                          <span className="no-document">Not uploaded</span>
                        )}
                      </div>
                      <div className="document-item">
                        <label>Resume</label>
                        {selectedEmployee.documents?.resume ? (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() =>
                              downloadDocument(
                                selectedEmployee.documents.resume,
                                "Resume"
                              )
                            }
                          >
                            <i className="fas fa-download"></i>
                            Download
                          </button>
                        ) : (
                          <span className="no-document">Not uploaded</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div
            className="modal-overlay"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <div
              className="modal-content delete-confirm-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Confirm Delete</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-body">
                <p>
                  Are you sure you want to delete this employee? This action
                  cannot be undone.
                </p>
                <div className="warning-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <p className="warning-text">
                  All employee data including documents, attendance records, and
                  task history will be permanently removed.
                </p>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteEmployee}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Delete Employee"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmployeeManagement;
